import { useState, useEffect, useRef } from 'react';
import { api } from '../lib/api.js';
import { fmtClock, fmtClockNa, ymdLocal, timeToMinutes } from '../lib/format.js';
import { locationKeyFromName, isoMondayOf } from '../lib/schedule.js';
import { offerSwapOnConflict } from '../lib/shift-swap.js';
import { shiftWarningLine } from '../lib/shift-warnings.js';
import { BuildScheduleDayGantt } from './BuildScheduleDayGantt.jsx';
import { ShiftPill } from './ShiftPill.jsx';
import { ShiftEditor } from './ShiftEditor.jsx';
import { TemplatesModal } from './TemplatesModal.jsx';
import { ScheduleToolbar } from './ScheduleToolbar.jsx';
import { WeeklyHoursBadge } from './WeeklyHoursBadge.jsx';

// ─── Schedule Writer (Round 12) ─────────────────────────────────────────────
// Manager-only week grid for drafting shifts. One location at a time
// (location pill bar drives which one). Week navigation prev/next/today.
//
// Data flow:
//   - GET /api/shifts/:loc/week?week_start  → list of shifts (app + square)
//   - POST /api/shifts                      → create draft (assigned or open)
//   - PUT /api/shifts/:id                   → update
//   - DELETE /api/shifts/:id                → delete
//   - POST /api/shifts/pull-square/:loc     → mirror Square published shifts
//
// Each shift carries a `warnings` array surfaced as a small badge in the
// cell when the assignment overlaps an availability change.
export function BuildScheduleView({ location, user }) {
  // CJ 2026-05-16: previously this component early-returned a "rotate
  // your phone" splash when isNarrow, but the early return was BEFORE
  // the rest of the hooks. Rotating phone changed the hook count between
  // renders and crashed the React tree to a white screen. Fix: all hooks
  // run unconditionally; in portrait we force Day view (which fits) and
  // hide the Week toggle.
  const [isNarrow, setIsNarrow] = useState(() => window.innerWidth < 720);
  useEffect(() => {
    function onResize() { setIsNarrow(window.innerWidth < 720); }
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  const headers = { 'x-user-id': String(user.id) };
  const [weekStart, setWeekStart] = useState(() => isoMondayOf(new Date()));
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [unavailable, setUnavailable] = useState({});
  const [crossLocation, setCrossLocation] = useState({});
  // Restricted-date events for the visible week. Fed by /api/events
  // filtered to this location. We render a yellow banner per affected
  // day so the manager sees the parade/marathon/etc before scheduling.
  const [restrictedEvents, setRestrictedEvents] = useState([]);
  useEffect(() => {
    if (!location?.id || !weekStart) return;
    const end = (() => {
      const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() + 6);
      return d.toISOString().slice(0, 10);
    })();
    const params = new URLSearchParams({
      location_id: String(location.id), from: weekStart, to: end,
    });
    api('GET', `/api/events?${params.toString()}`)
      .then((r) => {
        const list = Array.isArray(r?.events) ? r.events : [];
        setRestrictedEvents(list.filter((e) => e.staffing_restricted));
      })
      .catch(() => setRestrictedEvents([]));
  }, [location?.id, weekStart]);
  // Round 16: Day vs Week view toggle. CJ 2026-05-16: on portrait we
  // force-render day view so the grid fits on a phone.
  const [viewModeRaw, setViewMode] = useState('week');
  const viewMode = isNarrow ? 'day' : viewModeRaw;
  // CJ feedback 2026-06-15: some managers prefer to read the schedule
  // by staff availability (green = open slots), others by restrictions
  // (red = blocked). Per-manager preference; persists in localStorage so
  // switching back to Build Schedule keeps the layout the same.
  const [showAsAvailability, setShowAsAvailability] = useState(() => {
    try { return localStorage.getItem('monarchBuildScheduleAvailMode') === 'availability'; }
    catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('monarchBuildScheduleAvailMode', showAsAvailability ? 'availability' : 'restrictions'); }
    catch (_) {}
  }, [showAsAvailability]);
  const [dayDate, setDayDate] = useState(() => ymdLocal(new Date()));
  // Round 22: publish + templates
  const [publishBusy, setPublishBusy] = useState(false);
  // W-103 fix: typed-confirm modal replaces native confirm() so the
  // mutating publish action can't be missed or auto-dismissed.
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [publishConfirmText, setPublishConfirmText] = useState('');
  // Status line for publish / clear-week (was pullMsg; the pull-from-Square
  // affordance was removed in a68df7d but publishWeek/clearWeek still wrote to
  // its now-undefined setter, throwing a ReferenceError before the fetch and
  // leaving the button stuck on "Publishing…" — CJ report 2026-07-28).
  const [publishMsg, setPublishMsg] = useState('');
  const [templateUI, setTemplateUI] = useState(null); // 'list' | 'save'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState(null); // { mode:'create'|'edit', date, employee_id, shift }
  const [locations, setLocations] = useState([]); // for backup-staff "home café" tags
  // Drag-and-drop state. The drag payload lives in a useRef because state
  // updates from onDragStart aren't synchronously visible inside the next
  // event's closure — the drop handler would read a stale `null` if we
  // used useState. dropTargetKey is fine as state since it only feeds
  // re-renders for the highlight outline.
  const dragShiftRef = React.useRef(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);
  const locKey = locationKeyFromName(location?.name);

  async function load() {
    if (!locKey) return;
    // Only show the spinner on the first load. Subsequent reloads (after
    // a save / drag-drop / delete) keep the existing grid visible while
    // we fetch — eliminates the full-screen flash CJ saw between every
    // edit. The data swaps in once the request resolves.
    setError('');
    if (shifts.length === 0) setLoading(true);
    try {
      const [r, u, locs] = await Promise.all([
        api('GET', `/api/shifts/${locKey}/week?week_start=${weekStart}`, null, headers),
        api('GET', '/api/users'),
        api('GET', '/api/locations', null, headers).catch(() => []),
      ]);
      setLocations(locs || []);
      setShifts(r.shifts || []);
      setUnavailable(r.unavailable || {});
      setCrossLocation(r.crossLocation || {});
      // Sort by name; we keep the full roster so the assignment dropdown
      // can pick anyone (e.g., for cross-location borrows). The grid
      // below uses an isAuthorizedHere helper to gray out staff not
      // assigned to this location while still rendering them.
      const sorted = (u || []).slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setUsers(sorted);
    } catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }
  function isAuthorizedHere(u) {
    if (!u?.location_ids) return false;
    try { return JSON.parse(u.location_ids).includes(location.id); } catch { return false; }
  }
  useEffect(() => { load(); }, [locKey, weekStart, location?.id]);

  function shiftWeek(weeks) {
    const d = new Date(weekStart + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + weeks * 7);
    setWeekStart(ymdLocal(d));
  }
  const thisMon = isoMondayOf(new Date());
  const days = (() => {
    const out = [];
    const d = new Date(weekStart + 'T00:00:00Z');
    for (let i = 0; i < 7; i++) {
      const ymd = d.toISOString().slice(0, 10);
      const label = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][i];
      out.push({ ymd, label, day: d.getUTCDate() });
      d.setUTCDate(d.getUTCDate() + 1);
    }
    return out;
  })();

  // Group shifts by (employee_id, date) for the assigned grid; null bucket
  // collects unassigned shifts. Open shifts also get bucketed by date so
  // we can render an "Open" row at the top of the grid that mirrors the
  // staff rows below — one cell per day, each showing the open shifts
  // for that day.
  const assignedByKey = new Map();
  const openShifts = [];
  const openShiftsByDate = new Map();
  for (const s of shifts) {
    if (s.employee_id) {
      const k = `${s.employee_id}|${s.shift_date}`;
      if (!assignedByKey.has(k)) assignedByKey.set(k, []);
      assignedByKey.get(k).push(s);
    } else {
      openShifts.push(s);
      if (!openShiftsByDate.has(s.shift_date)) openShiftsByDate.set(s.shift_date, []);
      openShiftsByDate.get(s.shift_date).push(s);
    }
  }

  // Total scheduled hours per user across the visible week — used in the
  // staff row label so the manager can spot OT before it ships. Unpaid
  // breaks (typically a 30-min meal break) are subtracted; paid breaks
  // (10-min rest) leave the count alone.
  function hoursForUser(uid) {
    let mins = 0;
    for (const s of shifts) {
      if (s.employee_id !== uid) continue;
      const sm = timeToMinutes(s.start_time);
      const em = timeToMinutes(s.end_time);
      if (sm == null || em == null) continue;
      let dur = em - sm;
      if (s.break_minutes && !s.break_paid) dur -= Number(s.break_minutes);
      if (dur > 0) mins += dur;
    }
    return mins / 60;
  }

  // Drag-and-drop handler. `targetEmployeeId` is null when the drop landed
  // on the open-shifts row. `copy` is true if Alt/Option was held.
  async function handleDrop(targetEmployeeId, targetYmd, copy) {
    const s = dragShiftRef.current;
    dragShiftRef.current = null;
    setDropTargetKey(null);
    if (!s) return;
    // No-op if the user dropped onto the same cell.
    if (s.employee_id === targetEmployeeId && s.shift_date === targetYmd) return;
    try {
      if (copy) {
        // Duplicate to the new (employee, date) — same start/end.
        await api('POST', '/api/shifts', {
          location_id: location.id,
          employee_id: targetEmployeeId,   // null = open
          shift_date: targetYmd,
          start_time: s.start_time,
          end_time:   s.end_time,
          role:       s.role || null,
          notes:      s.notes || null,
          status:     s.status,            // preserve draft/published
        }, headers);
      } else {
        await api('PUT', `/api/shifts/${s.id}`, {
          ...s,
          employee_id: targetEmployeeId,   // null = open
          shift_date: targetYmd,
        }, headers);
      }
      load();
    } catch (e) {
      // Dragging onto an occupied row is the natural "swap these two" gesture.
      if (await offerSwapOnConflict(e.message, copy ? null : s.id, headers)) load();
    }
  }

  // Wipe the entire visible week. Confirms loud + clear because this
  // also removes published shifts from Square (mirrors via the same
  // deleteScheduledShift flow as single-shift delete) and notifies
  // every affected employee. There's no undo.
  const [clearBusy, setClearBusy] = useState(false);
  async function clearWeek() {
    const totalShifts = shifts.length;
    if (totalShifts === 0) { alert('Nothing scheduled this week.'); return; }
    const publishedCount = shifts.filter(s => s.status === 'published').length;
    const msg = publishedCount > 0
      ? `Delete ALL ${totalShifts} shifts this week? ${publishedCount} are already published — assigned employees will be notified. This cannot be undone.`
      : `Delete ALL ${totalShifts} draft shifts this week? This cannot be undone.`;
    if (!confirm(msg)) return;
    if (publishedCount > 0 && !confirm('Are you absolutely sure? Type confirm in the next prompt to proceed.')) return;
    if (publishedCount > 0) {
      const t = prompt('Type CLEAR to confirm wiping the entire week.');
      if (t !== 'CLEAR') { alert('Cancelled.'); return; }
    }
    setClearBusy(true);
    try {
      const r = await api('POST', `/api/shifts/clear-week/${locKey}`, { week_start: weekStart }, headers);
      setPublishMsg(`${r.localDeleted} shift${r.localDeleted === 1 ? '' : 's'} removed`);
      load();
    } catch (e) {
      let m = e.message; try { m = JSON.parse(e.message).error; } catch {}
      alert(`Clear failed: ${m}`);
    }
    finally { setClearBusy(false); }
  }

  async function publishWeek() {
    setPublishConfirmOpen(false);
    setPublishConfirmText('');
    setPublishBusy(true); setPublishMsg('');
    try {
      const r = await api('POST', `/api/shifts/publish/${locKey}`,
        { week_start: weekStart }, headers);
      setPublishMsg(`Published ${r.published} shifts · ${r.employees_notified} staff notified.`);
      load();
    } catch (e) {
      let msg = e.message || String(e);
      try { const p = JSON.parse(msg); msg = p.error || msg; } catch {}
      setPublishMsg(`Publish failed: ${msg}`);
    }
    finally { setPublishBusy(false); }
  }

  function openCreateForCell(employee_id, ymd, prefill) {
    const def = users.find((u) => u.id === employee_id);
    setEditor({
      mode: 'create',
      form: {
        location_id: location.id,
        employee_id: employee_id || null,
        shift_date: ymd,
        start_time: (prefill && prefill.start_time) || '08:00',
        end_time:   (prefill && prefill.end_time)   || '14:00',
        role: def?.role || '',
        notes: '',
        break_minutes: 10,    // RI default: paid 10-min rest break
        break_paid: true,
        // Multi-day: when set, save creates one shift per date instead
        // of one. Pre-seeded with the cell's own date so the default is
        // single-day.
        extra_dates: [],
      },
    });
  }
  function openEdit(shift) {
    setEditor({
      mode: 'edit',
      form: {
        id: shift.id,
        location_id: shift.location_id,
        employee_id: shift.employee_id,
        shift_date: shift.shift_date,
        start_time: shift.start_time,
        end_time: shift.end_time,
        role: shift.role || '',
        notes: shift.notes || '',
        break_minutes: shift.break_minutes ?? 10,
        break_paid: shift.break_paid != null ? !!shift.break_paid : true,
      },
      source: shift.source,
    });
  }

  async function saveEditor() {
    const f = editor.form;
    try {
      let r;
      if (editor.mode === 'create') {
        const allDates = Array.from(new Set([f.shift_date, ...(f.extra_dates || [])])).sort();
        if (allDates.length > 1) {
          // Bulk-create — server returns { created, conflicts }; surface
          // skipped days so the manager isn't surprised.
          r = await api('POST', '/api/shifts/bulk', { ...f, dates: allDates }, headers);
          if (r.conflicts?.length) {
            alert(`${r.created.length} shift${r.created.length === 1 ? '' : 's'} created. Skipped (conflict): ${r.conflicts.map(c => c.date).join(', ')}`);
          }
        } else {
          r = await api('POST', '/api/shifts', f, headers);
        }
      } else {
        r = await api('PUT', `/api/shifts/${f.id}`, f, headers);
      }
      // CJ feedback 2026-06-15 followup: server computes availability +
      // TOR overlap warnings on every POST/PUT. Three response shapes
      // are in play:
      //   - single POST  { ok, shift: { ..., warnings: [...] } }
      //   - bulk POST    { ok, created: [{ ..., warnings: [...] }, ...] }
      //   - PUT          { ok, warnings: [...] }
      // Earlier code only read `r.warnings` / `r.created[0].warnings`
      // so the single-POST + bulk-POST warnings were silently dropped.
      // 2026-06-15 followup-2: extract from all three paths.
      const warnings = [
        ...(Array.isArray(r?.warnings) ? r.warnings : []),
        ...(Array.isArray(r?.shift?.warnings) ? r.shift.warnings : []),
        ...((Array.isArray(r?.created) ? r.created : []).flatMap(c => Array.isArray(c?.warnings) ? c.warnings : [])),
      ];
      if (Array.isArray(warnings) && warnings.length > 0) {
        const lines = warnings.map(shiftWarningLine);
        alert(`Shift saved — but heads up:\n\n${lines.join('\n')}\n\nThe employee is flagged unavailable for some or all of this slot.`);
      }
      setEditor(null); load();
    } catch (e) {
      // A collision with the target's own shift is offered as a swap, not just
      // refused — that dead end is what cost Gina a manual fix.
      if (await offerSwapOnConflict(e.message, editor.mode === 'edit' ? f.id : null, headers)) { setEditor(null); load(); }
    }
  }

  async function deleteShift(id) {
    const target = shifts.find((s) => s.id === id);
    const isPublished = target?.status === 'published';
    const msg = isPublished
      ? "Delete this published shift? The employee will be notified, and if it's already in Square it will be removed there too."
      : 'Delete this shift?';
    if (!confirm(msg)) return;
    try {
      await api('DELETE', `/api/shifts/${id}`, null, headers);
      load();
    } catch (e) { alert(e.message || 'Delete failed'); }
  }

  function shiftDay(days) {
    const d = new Date(dayDate + 'T12:00:00Z');
    d.setUTCDate(d.getUTCDate() + days);
    setDayDate(ymdLocal(d));
    // Snap weekStart to the Monday of the new day so navigating in Day
    // mode keeps the underlying week request aligned.
    setWeekStart(isoMondayOf(new Date(ymdLocal(d) + 'T12:00:00Z')));
  }

  return (
    <div className="space-y-3">
      {/* Restricted-date banner — events flagged staffing_restricted
          at this location for the current week. CJ feedback 2026-05-11. */}
      {restrictedEvents.length > 0 && (
        <div style={{ background: '#fef9ee', border: '1px solid #f5e6c8', borderLeft: '4px solid #d97706',
                        borderRadius: 8, padding: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#8e6c33',
                          textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
            🔒 Restricted staffing this week
          </div>
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#5a4220' }}>
            {restrictedEvents.map((e) => {
              const s = String(e.start_date).slice(0, 10);
              const en = String(e.end_date || e.start_date).slice(0, 10);
              const range = (en && en !== s)
                ? `${s} → ${en}`
                : new Date(s + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
              return (
                <li key={`${e.id}-${e.start_date}`}>
                  <strong>{range}:</strong> {e.title}
                  {e.notes && <span style={{ color: '#7a5b1a' }}> — {e.notes}</span>}
                </li>
              );
            })}
          </ul>
          <div style={{ fontSize: 11, color: '#92400e', marginTop: 6, fontStyle: 'italic' }}>
            Time-off requests + availability changes touching these dates require advance notice. Plan staffing carefully.
          </div>
        </div>
      )}

      {/* Toolbar */}
      <ScheduleToolbar
        viewMode={viewMode} setViewMode={setViewMode} isNarrow={isNarrow}
        weekStart={weekStart} setWeekStart={setWeekStart} thisMon={thisMon}
        dayDate={dayDate} setDayDate={setDayDate}
        shiftWeek={shiftWeek} shiftDay={shiftDay} location={location}
        showAsAvailability={showAsAvailability} setShowAsAvailability={setShowAsAvailability}
        openCreateForCell={openCreateForCell} setTemplateUI={setTemplateUI}
        clearWeek={clearWeek} clearBusy={clearBusy} shifts={shifts}
        publishBusy={publishBusy}
        setPublishConfirmText={setPublishConfirmText} setPublishConfirmOpen={setPublishConfirmOpen}
      />
      {publishMsg && (
        <div className="text-xs" style={{ color: publishMsg.startsWith('Publish failed') ? '#c62828' : '#37474f', marginTop: 6 }}>
          {publishMsg}
        </div>
      )}
      {publishConfirmOpen && (() => {
        const draftCount = shifts.filter((s) => s.status === 'draft').length;
        const assignedCount = new Set(
          shifts.filter((s) => s.status === 'draft' && s.employee_id).map((s) => s.employee_id)
        ).size;
        const okToConfirm = publishConfirmText.trim().toUpperCase() === 'PUBLISH';
        return (
          <div onClick={() => !publishBusy && setPublishConfirmOpen(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 10, padding: 22, maxWidth: 460, width: '100%',
                        borderTop: '4px solid #c62828' }}>
              <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>
                Publish schedule?
              </div>
              <div style={{ fontSize: 13, color: '#37474f', lineHeight: 1.5, marginBottom: 12 }}>
                You are about to publish <strong>{draftCount} draft shift{draftCount === 1 ? '' : 's'}</strong> for{' '}
                <strong>{location.name}</strong>, week of <strong>{weekStart}</strong>.
                {assignedCount > 0 && (
                  <> This will send a push + email notification to <strong>{assignedCount} staff member{assignedCount === 1 ? '' : 's'}</strong>.</>
                )}
              </div>
              <div style={{ fontSize: 12, color: '#757575', marginBottom: 10 }}>
                Type <code style={{ background: '#f5f5f5', padding: '1px 6px', borderRadius: 4 }}>PUBLISH</code> to confirm:
              </div>
              <input autoFocus value={publishConfirmText}
                onChange={(e) => setPublishConfirmText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && okToConfirm) publishWeek(); }}
                placeholder="PUBLISH"
                style={{ width: '100%', border: '1px solid #d0d4d9', borderRadius: 6, padding: 10,
                          fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 16 }} />
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                <button onClick={() => setPublishConfirmOpen(false)} disabled={publishBusy}
                  className="btn-secondary" style={{ fontSize: 13 }}>Cancel</button>
                <button onClick={publishWeek} disabled={!okToConfirm || publishBusy}
                  style={{ padding: '8px 16px', fontSize: 13, fontWeight: 700,
                            background: okToConfirm ? '#c62828' : '#e0e0e0',
                            color: '#fff', border: 'none', borderRadius: 6,
                            cursor: okToConfirm && !publishBusy ? 'pointer' : 'not-allowed',
                            opacity: publishBusy ? 0.5 : 1 }}>
                  {publishBusy ? 'Publishing…' : 'Yes, publish and notify staff'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {error && <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-xs text-red-700">{error}</div>
      </div>}

      {/* Grid */}
      {loading ? (
        <div className="card text-sm text-gray-500">Loading…</div>
      ) : viewMode === 'day' ? (
        <BuildScheduleDayGantt
          location={location}
          dayDate={dayDate}
          users={users}
          shifts={shifts}
          unavailable={unavailable}
          crossLocation={crossLocation}
          isAuthorizedHere={isAuthorizedHere}
          openCreateForCell={openCreateForCell}
          openEdit={openEdit}
          showAsAvailability={showAsAvailability}
        />
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fafbfc', borderBottom: '1px solid #e0e0e0' }}>
                <th style={{ padding: '8px', textAlign: 'left', minWidth: 140, position: 'sticky', left: 0, background: '#fafbfc', zIndex: 1 }}>
                  Staff
                </th>
                {days.map((d) => (
                  <th key={d.ymd}
                    style={{ padding: '8px', textAlign: 'center', minWidth: 110, color: '#37474f' }}>
                    <div style={{ fontWeight: 600 }}>{d.label}</div>
                    <div style={{ fontSize: 11, color: '#9e9e9e' }}>{d.day}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Open shifts row — sits above the staff so a manager can
                  see at a glance which days still have unassigned coverage,
                  and tap a cell to add another or click an existing pill
                  to assign a person. Mirrors the staff-row layout below. */}
              <tr style={{ borderBottom: '2px solid #ffe082', background: '#fffdf3' }}>
                <td style={{ padding: '6px 8px', fontWeight: 700, position: 'sticky', left: 0,
                              background: '#fffdf3', zIndex: 1, color: '#8a5a00' }}>
                  <div style={{ fontSize: 13 }}>Open shifts</div>
                  <div style={{ fontSize: 10, color: '#a16207' }}>Unassigned</div>
                </td>
                {days.map((d) => {
                  const list = openShiftsByDate.get(d.ymd) || [];
                  const isEmpty = list.length === 0;
                  const dropKey = `open|${d.ymd}`;
                  const isDropTarget = dropTargetKey === dropKey;
                  return (
                    <td key={d.ymd}
                      onClick={() => isEmpty && openCreateForCell(null, d.ymd)}
                      onDragOver={(e) => { e.preventDefault(); setDropTargetKey(dropKey); }}
                      onDragLeave={() => isDropTarget && setDropTargetKey(null)}
                      onDrop={(e) => { e.preventDefault(); handleDrop(null, d.ymd, e.altKey); }}
                      title={isEmpty ? 'Click to add an open shift' : `${list.length} open shift${list.length === 1 ? '' : 's'}`}
                      style={{ padding: 4, verticalAlign: 'top', minHeight: 50, height: 50,
                                cursor: isEmpty ? 'pointer' : 'default',
                                background: isDropTarget ? '#fff3c4' : (isEmpty ? '#fffdf3' : '#fff8e1'),
                                outline: isDropTarget ? '2px dashed var(--gold, #c8a25b)' : 'none',
                                borderLeft: '1px solid #ffecb3' }}>
                      {list.map((s) => <ShiftPill key={s.id} shift={s} onClick={() => openEdit(s)}
                        draggable
                        onDragStart={(e) => { dragShiftRef.current = s; e.dataTransfer.effectAllowed = 'copyMove'; e.dataTransfer.setData('text/plain', String(s.id)); }}
                        onDragEnd={() => { dragShiftRef.current = null; setDropTargetKey(null); }} />)}
                      {isEmpty && (
                        <div style={{ fontSize: 10, color: '#bfa75a', textAlign: 'center', padding: '8px 0' }}>
                          + add open
                        </div>
                      )}
                      {!isEmpty && (
                        <button onClick={(e) => { e.stopPropagation(); openCreateForCell(null, d.ymd); }}
                          style={{ display: 'block', width: '100%', marginTop: 2,
                                    padding: '2px 4px', fontSize: 10, color: '#bfa75a',
                                    background: 'transparent', border: '1px dashed #ffe082',
                                    borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                          + another
                        </button>
                      )}
                    </td>
                  );
                })}
              </tr>
              {(() => {
                // Render only staff authorized at this location as rows;
                // the modal dropdown still shows everyone so a manager can
                // pull a borrow in. Avoids the row sea of unrelated staff.
                const locName = new Map((locations || []).map((l) => [l.id, l.name]));
                const authorized = users.filter(isAuthorizedHere);
                if (authorized.length === 0) return (
                  <tr><td colSpan={8} style={{ padding: 16, color: '#9e9e9e', textAlign: 'center', fontStyle: 'italic' }}>
                    No staff authorized at this location.
                  </td></tr>
                );
                // CJ 2026-07-29: regulars (primary location = this café) on top,
                // a divider, then backup staff who can cover from other cafés.
                const roleRank = (r) => { const s = (r || '').toLowerCase(); return s.includes('owner') ? 0 : s.includes('manager') ? 1 : s.includes('lead') ? 2 : s.includes('barista') ? 3 : s.includes('roast') ? 4 : 5; };
                const byRoleName = (a, b) => roleRank(a.role) - roleRank(b.role) || (a.name || '').localeCompare(b.name || '');
                const primaryRows = authorized.filter((u) => u.primary_location_id === location.id).sort(byRoleName);
                const backupRows = authorized.filter((u) => u.primary_location_id !== location.id).sort(byRoleName);
                const items = backupRows.length ? [...primaryRows, { __divider: true }, ...backupRows] : primaryRows;
                return items.map((u) => {
                  if (u.__divider) return (
                    <tr key="__backup_divider"><td colSpan={days.length + 1}
                      style={{ padding: '4px 8px', fontSize: 10, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: '#b45c3a', background: '#fafbfa', borderTop: '2px dashed #d4dbd3', borderBottom: '1px solid #f0f0f0', position: 'sticky', left: 0 }}>
                      ↓ Backup · can cover, based at another café
                    </td></tr>
                  );
                  const isBackup = u.primary_location_id !== location.id;
                  const homeName = isBackup ? locName.get(u.primary_location_id) : null;
                  const elsewhere = crossLocation[u.id] || [];
                  return (
                <tr key={u.id} style={{ borderBottom: '1px solid #f0f0f0', background: isBackup ? '#fafbfa' : undefined }}>
                  <td style={{ padding: '6px 8px', fontWeight: 600, position: 'sticky', left: 0, background: isBackup ? '#fafbfa' : '#fff', zIndex: 1 }}>
                    <div style={{ fontSize: 13, color: isBackup ? '#5c665a' : undefined }}>{u.name}</div>
                    <div style={{ fontSize: 10, color: '#9e9e9e' }}>{u.role}{homeName ? ` · ${homeName}` : ''}</div>
                    <WeeklyHoursBadge hours={hoursForUser(u.id)} elsewhere={elsewhere} min={u.desired_hours_min} max={u.desired_hours_max} />
                    {elsewhere.length > 0 && (
                      <div title={elsewhere.map((x) => `${x.date.slice(5)}: ${x.location_name} ${fmtClock(x.start_time)}–${fmtClock(x.end_time)}`).join('\n')}
                        style={{ fontSize: 9, fontWeight: 700, color: '#6a1b9a',
                                  background: '#f3e5f5', border: '1px solid #ce93d8',
                                  padding: '1px 5px', borderRadius: 999, marginTop: 2,
                                  display: 'inline-block' }}>
                        +{elsewhere.length} at {[...new Set(elsewhere.map((x) => x.location_name))].join(', ')}
                      </div>
                    )}
                  </td>
                  {days.map((d) => {
                    const list = assignedByKey.get(`${u.id}|${d.ymd}`) || [];
                    const unav = unavailable[`${u.id}|${d.ymd}`] || [];
                    const fullDay = unav.find((w) => w.kind === 'full_day_off' || w.kind === 'tor_full_day');
                    const partialBlocks = unav.filter((w) => w.kind === 'time_range' || w.kind === 'tor_partial');
                    // Migration 076: positive availability windows render as
                    // soft-green "AVAIL" cards alongside the orange NA cards.
                    const preferredFullDay = unav.find((w) => w.kind === 'preferred_full_day');
                    const preferredWindows = unav.filter((w) => w.kind === 'preferred_window');
                    // Round 22: surface cross-location shifts in the cell
                    // so a manager doesn't accidentally schedule over them.
                    const elseToday = (crossLocation[u.id] || []).filter((x) => x.date === d.ymd);
                    const hasElse = elseToday.length > 0;
                    const isEmpty = list.length === 0;
                    // Availability-emphasis mode inverts the cell tint:
                    // open slots glow soft green, blocked slots fade to
                    // neutral gray. Restrictions-mode keeps the original
                    // red/yellow/purple shading from the schedule's
                    // original release.
                    const isFullyOpen = isEmpty && !fullDay && !hasElse && partialBlocks.length === 0;
                    const cellBg = isEmpty
                      ? (showAsAvailability
                          ? (isFullyOpen ? '#dcfce7' : '#f3f4f6')
                          : (fullDay ? '#fff5f5'
                              : hasElse ? '#f3e5f5'
                              : (partialBlocks.length ? '#fff8e1' : '#fafbfc')))
                      : '#fff';
                    const titleParts = [];
                    if (fullDay) titleParts.push(`OFF · ${fullDay.kind === 'tor_full_day' ? 'Approved time off' : 'Availability change'}${fullDay.note ? ' (' + fullDay.note + ')' : ''}`);
                    if (partialBlocks.length) titleParts.push(`Partial conflict: ${partialBlocks.map(p => p.range).join(', ')}`);
                    if (hasElse) titleParts.push(elseToday.map((x) => `${x.location_name} ${fmtClock(x.start_time)}–${fmtClock(x.end_time)}`).join('; '));
                    const dropKey = `${u.id}|${d.ymd}`;
                    const isDropTarget = dropTargetKey === dropKey;
                    return (
                      <td key={d.ymd} onClick={() => isEmpty && openCreateForCell(u.id, d.ymd)}
                        onDragOver={(e) => { e.preventDefault(); setDropTargetKey(dropKey); }}
                        onDragLeave={() => isDropTarget && setDropTargetKey(null)}
                        onDrop={(e) => { e.preventDefault(); handleDrop(u.id, d.ymd, e.altKey); }}
                        title={titleParts.join(' · ')}
                        style={{ padding: 4, verticalAlign: 'top', minHeight: 50, height: 50,
                                  cursor: isEmpty ? 'pointer' : 'default',
                                  background: isDropTarget ? '#e3f2fd' : cellBg,
                                  outline: isDropTarget ? '2px dashed #1976d2' : 'none',
                                  borderLeft: '1px solid #f5f5f5' }}>
                        {list.map((s) => <ShiftPill key={s.id} shift={s} onClick={() => openEdit(s)}
                          draggable
                          onDragStart={(e) => { dragShiftRef.current = s; e.dataTransfer.effectAllowed = 'copyMove'; e.dataTransfer.setData('text/plain', String(s.id)); }}
                          onDragEnd={() => { dragShiftRef.current = null; setDropTargetKey(null); }} />)}
                        {isEmpty && fullDay && (
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#c62828',
                                          textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 4px' }}>
                            OFF{fullDay.kind === 'tor_full_day' ? ' (TOR)' : ''}
                          </div>
                        )}
                        {isEmpty && !fullDay && hasElse && (
                          <div style={{ fontSize: 9, fontWeight: 700, color: '#6a1b9a',
                                          padding: '2px 4px',
                                          background: 'repeating-linear-gradient(135deg, transparent, transparent 4px, rgba(106,27,154,0.12) 4px, rgba(106,27,154,0.12) 8px)',
                                          borderRadius: 3 }}>
                            @ {elseToday.map((x) => `${x.location_name.slice(0,4)} ${fmtClock(x.start_time)}`).join(', ')}
                          </div>
                        )}
                        {isEmpty && !fullDay && !hasElse && partialBlocks.length > 0 && (
                          // CJ 2026-06-15: partial restrictions used to read
                          // "⚠ 13:00-15:00" — manager had to mentally parse
                          // 24h time + remember what the icon meant. New
                          // shape "NA 1pm–3pm" puts the meaning first and
                          // matches the clock format used elsewhere. All
                          // partials are listed (not just the first).
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#a16207',
                                          padding: '2px 4px', lineHeight: 1.25 }}>
                            NA {partialBlocks.map(p => {
                              const [s, e] = (p.range || '').split('-');
                              return `${fmtClockNa(s)}–${fmtClockNa(e)}`;
                            }).join(', ')}
                          </div>
                        )}
                        {/* Positive availability cards (Square-style
                            "Available · All day" / "AVAIL 9am–5pm"). Migration
                            076 added them; render only when there's no
                            higher-priority red OFF overlay claiming the
                            cell. */}
                        {isEmpty && !fullDay && preferredFullDay && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#166534',
                                          padding: '2px 4px', lineHeight: 1.25 }}>
                            AVAIL · all day
                          </div>
                        )}
                        {isEmpty && !fullDay && !preferredFullDay && preferredWindows.length > 0 && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: '#166534',
                                          padding: '2px 4px', lineHeight: 1.25 }}>
                            AVAIL {preferredWindows.map(p => {
                              const [s, e] = (p.range || '').split('-');
                              return `${fmtClockNa(s)}–${fmtClockNa(e)}`;
                            }).join(', ')}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      )}

      {/* Open shifts tag-cloud — only in day view (where the per-day grid
          row above the staff isn't rendered). Week view shows opens as a
          dedicated top row inside the grid; this card would be redundant. */}
      {viewMode === 'day' && openShifts.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 13, fontWeight: 700, color: '#37474f', marginBottom: 6 }}>
            Open shifts ({openShifts.length})
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {openShifts.map((s) => (
              <div key={s.id} onClick={() => openEdit(s)}
                style={{ padding: '6px 10px', border: '1.5px dashed #ef6c00', background: '#fff8e1',
                          borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 600 }}>
                {s.shift_date.slice(5)} · {fmtClock(s.start_time)}–{fmtClock(s.end_time)}{s.role ? ` · ${s.role}` : ''}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Editor modal */}
      {editor && (
        <ShiftEditor
          editor={editor}
          users={users}
          onChange={(form) => setEditor((e) => ({ ...e, form }))}
          onSave={saveEditor}
          onClose={() => setEditor(null)}
          onDelete={editor.mode === 'edit' ? () => { deleteShift(editor.form.id); setEditor(null); } : null}
        />
      )}

      {templateUI && (
        <TemplatesModal
          location={location}
          weekStart={weekStart}
          currentUser={user}
          shiftCount={shifts.length}
          onClose={() => setTemplateUI(null)}
          onApplied={() => { setTemplateUI(null); load(); }}
        />
      )}

    </div>
  );
}
